/**
 * Formatação de telefone para exibição.
 *
 * Os formatadores anteriores assumiam que todo número de 12 ou 13 dígitos era
 * brasileiro e o quebravam em "+DD (DD) NNNNN-NNNN". Com um número estrangeiro
 * isso produzia lixo: Portugal "351924270875" virava "+35 (19) 2427-0875".
 *
 * Regra: só formata no padrão brasileiro quando o número realmente começa com
 * o DDI 55; nos demais casos exibe "+" seguido dos dígitos, que é legível e
 * sempre correto.
 */

import { i18n } from "../translate/i18n";

/** DDDs brasileiros válidos (Anatel). */
const BRAZILIAN_AREA_CODES = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19,
  21, 22, 24, 27, 28,
  31, 32, 33, 34, 35, 37, 38,
  41, 42, 43, 44, 45, 46, 47, 48, 49,
  51, 53, 54, 55,
  61, 62, 63, 64, 65, 66, 67, 68, 69,
  71, 73, 74, 75, 77, 79,
  81, 82, 83, 84, 85, 86, 87, 88, 89,
  91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

export const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

/** O número está em E.164 brasileiro (55 + DDD válido + 8 ou 9 dígitos)? */
export const isBrazilianNumber = (value) => {
  const digits = onlyDigits(value);
  if (!digits.startsWith("55")) return false;
  if (digits.length !== 12 && digits.length !== 13) return false;
  return BRAZILIAN_AREA_CODES.has(Number(digits.slice(2, 4)));
};

/**
 * Formata para exibição. Brasileiros ganham o formato local; estrangeiros são
 * exibidos como "+<dígitos>".
 */
export const formatPhoneNumber = (value, fallback = i18n.t("phoneFormat.notFound")) => {
  const digits = onlyDigits(value);
  if (!digits) return fallback;

  if (isBrazilianNumber(digits)) {
    const areaCode = digits.slice(2, 4);
    const subscriber = digits.slice(4);
    const middle = subscriber.length === 9 ? 5 : 4;
    return `+55 (${areaCode}) ${subscriber.slice(0, middle)}-${subscriber.slice(middle)}`;
  }

  return `+${digits}`;
};

/**
 * Separa um número em DDD + assinante, para as telas de Cliente que guardam os
 * campos legados. Estrangeiro não tem DDD, então devolve vazio em vez de
 * fatiar o número e produzir dados incorretos.
 */
export const splitBrazilianPhone = (value) => {
  const digits = onlyDigits(value);
  if (!isBrazilianNumber(digits)) return { DDDCELULAR: "", CELULAR: "" };

  return {
    DDDCELULAR: digits.slice(2, 4),
    CELULAR: digits.slice(4, 13),
  };
};
